class Store {
  readonly dbp: Promise<IDBDatabase>
  private maxRetries = 3
  private retryDelay = 100 // ms

  constructor(
    dbName = 'keyval-store',
    readonly storeName = 'keyval'
  ) {
    this.dbp = new Promise((resolve, reject) => {
      const openreq = indexedDB.open(dbName, 1)
      openreq.onerror = () => reject(openreq.error)
      openreq.onsuccess = () => resolve(openreq.result)

      // First time setup: create an empty object store
      openreq.onupgradeneeded = () => {
        openreq.result.createObjectStore(storeName)
      }
    })
  }

  private async withRetry<T>(operation: () => Promise<T>): Promise<T> {
    let lastError: Error | undefined

    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      try {
        return await operation()
      } catch (error) {
        lastError = error as Error
        if (error instanceof Error && error.name === 'InvalidStateError') {
          await new Promise(resolve =>
            setTimeout(resolve, this.retryDelay * Math.pow(2, attempt))
          )
          continue
        }
        throw error
      }
    }

    throw lastError
  }

  withIDBStore(
    type: any,
    callback: (store: IDBObjectStore) => void
  ): Promise<void> {
    return this.withRetry(() =>
      this.dbp.then(
        db =>
          new Promise<void>((resolve, reject) => {
            let transaction: IDBTransaction | null = null
            try {
              transaction = db.transaction(this.storeName, type)
              transaction.oncomplete = () => resolve()
              transaction.onabort = transaction.onerror = () => {
                const error =
                  transaction?.error || new Error('Transaction failed')
                reject(error)
              }
              callback(transaction.objectStore(this.storeName))
            } catch (error) {
              reject(error)
            }
          })
      )
    )
  }
}

let store: Store

function getDefaultStore() {
  if (!store) {
    store = new Store()
  }
  return store
}

export function get<Type>(key: any, store = getDefaultStore()): Promise<Type> {
  let req: IDBRequest
  return store
    .withIDBStore('readonly', store => {
      req = store.get(key)
    })
    .then(() => req.result)
}

export function set(
  key: any,
  value: any,
  store = getDefaultStore()
): Promise<void> {
  return store.withIDBStore('readwrite', store => {
    store.put(value, key)
  })
}

export function del(key: any, store = getDefaultStore()): Promise<void> {
  return store.withIDBStore('readwrite', store => {
    store.delete(key)
  })
}

export function clear(store = getDefaultStore()): Promise<void> {
  return store.withIDBStore('readwrite', store => {
    store.clear()
  })
}
